import { gql } from "@apollo/client";
import { CurrencyCode } from "@asmbl/shared/constants";
import { Money, zero } from "@asmbl/shared/money";
import { Switch, TableBody, TableCell, TableRow, Tooltip } from "@mui/material";
import makeStyles from "../../../../makeStylesV4Compat";
import clsx from "clsx";
import { useState } from "react";
import {
  MatrixCard_matrixRange,
  MatrixTypeEnum,
} from "src/__generated__/graphql";
import { AssembleTypography } from "src/components/AssembleTypography";
import { getSimpleCashLabel } from "src/models/Currency";
import { noop } from "src/test-helpers";
import { GRAY_1, GRAY_3, GRAY_5, GRAY_6, WHITE } from "src/theme";
import { MatrixEditButton } from "../MatrixEditButton";
import { RangeDraft, useMultipleMeritMatrices } from "../useMultipleMatrices";
import { MatrixCell } from "./MatrixCell";
import { MatrixRangeCell } from "./MatrixRangeCell";
import { useTableStyles } from "./tableStyles";

type Props = {
  cells: { label: string | number; id?: number | string }[][];
  matrixId: number;
  orderedRanges: (MatrixCard_matrixRange | RangeDraft)[];
  matrixType?: MatrixTypeEnum;
  estimatedCost?: Money;
};

const useStyles = makeStyles((theme) => ({
  table: {
    borderSpacing: 0,
  },
  tableTitle: {
    color: GRAY_3,
    marginBottom: theme.spacing(1),
  },
  titleCell: {
    padding: `${theme.spacing(0.75)} ${theme.spacing(1.25)} 0`,
  },
  relative: {
    position: "relative",
  },
  tableHead: {
    backgroundColor: WHITE,
    border: `1px solid ${GRAY_6}`,
    borderRadius: "5px 5px 0 0",
    display: "block",
    height: "30px",
    marginTop: theme.spacing(5),
    marginBottom: "-1px",
    width: "100%",
  },
  perfRatingHeader: {
    marginBottom: theme.spacing(-2),
  },
  perfSubcopy: {
    marginTop: theme.spacing(2),
  },
  spacer: {
    height: theme.spacing(2),
  },
  prefillContainer: {
    backgroundColor: WHITE,
    border: `solid 1px ${GRAY_5}`,
    borderRadius: 5,
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "space-between",
  },
  noSidePadding: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  rightAlign: {
    textAlign: "right",
  },
  prefillCell: {
    padding: `${theme.spacing(3)} 0 0 0`,
    cursor: "pointer",
  },
}));

export function Matrix({
  cells,
  matrixId,
  orderedRanges,
  matrixType,
  estimatedCost,
}: Props) {
  const classes = useStyles();
  const tableClasses = useTableStyles();
  const [showAddRow, setShowAddRow] = useState<boolean>(false);
  const {
    onAddPerfRatingOption,
    onAddGuidanceRange,
    onToggleShouldPrefillWithGuidance,
    shouldPrefillWithGuidance,
    meta: { loading },
  } = useMultipleMeritMatrices();

  return (
    <table className={classes.table}>
      <TableRow>
        <TableCell className={tableClasses.muiCellOverride} />
        <TableCell
          className={tableClasses.muiCellOverride}
          colSpan={cells.at(0)?.length ?? 1}
        >
          <AssembleTypography
            variant="productParagraphLarge"
            textColor={GRAY_1}
          >
            Compa-ratio Range
          </AssembleTypography>
          <AssembleTypography variant="productSmall">
            If a compa-ratio falls on a specific value in a column, it will be
            included in that column.
          </AssembleTypography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          className={clsx(tableClasses.muiCellOverride, tableClasses.shortCell)}
        >
          <AssembleTypography
            variant="productParagraphLarge"
            className={classes.perfRatingHeader}
          >
            Performance Rating
          </AssembleTypography>
          <AssembleTypography
            variant="productSmall"
            className={classes.perfSubcopy}
          >
            Ratings used in this cycle.
          </AssembleTypography>
        </TableCell>
        {orderedRanges.map((range, idx) => (
          <MatrixRangeCell
            key={`matrix-range-cell-${range.id}`}
            range={range}
            idx={idx}
            orderedRangesCount={orderedRanges.length}
            nextRangeId={orderedRanges[idx + 1]?.id}
          />
        ))}
      </TableRow>
      <TableRow className={classes.spacer} />
      {cells.length ? (
        <TableBody className={classes.relative}>
          {cells.map((cellRow, rowIdx) => (
            <TableRow
              key={`matrix-row-${cellRow[0].id ?? rowIdx}`}
              className={classes.relative}
              onMouseEnter={
                rowIdx === cells.length - 1 ? () => setShowAddRow(true) : noop
              }
              onMouseLeave={
                rowIdx === cells.length - 1 ? () => setShowAddRow(false) : noop
              }
            >
              {cellRow.map((cell, cellIdx) => (
                <MatrixCell
                  key={`matrix-cell-${cellIdx === 0 ? "performance" : "guidance"}-${cell.id ?? ""}`}
                  cellIdx={cellIdx}
                  rowIdx={rowIdx}
                  cell={cell}
                  cells={cells}
                  cellRowLength={cellRow.length}
                  matrixId={matrixId}
                />
              ))}
              {showAddRow && rowIdx === cells.length - 1 ? (
                <MatrixEditButton
                  position="bottom"
                  actionType="add"
                  onClick={onAddPerfRatingOption}
                  disabled={loading}
                />
              ) : null}
            </TableRow>
          ))}
          <MatrixEditButton
            position="right"
            actionType="add"
            onClick={onAddGuidanceRange}
          />
          <TableRow>
            <TableCell
              className={clsx(
                tableClasses.muiCellOverride,
                tableClasses.shortCell
              )}
            />
            <TableCell
              className={clsx([
                tableClasses.cell,
                tableClasses.muiCellOverride,
                tableClasses.metricsCell,
              ])}
              colSpan={orderedRanges.length}
            >
              {matrixType === MatrixTypeEnum.MERIT && (
                <Tooltip
                  title={
                    estimatedCost?.value == null || estimatedCost.value === 0
                      ? "Save your guidance and make sure employees have performance ratings to view guidance"
                      : ""
                  }
                >
                  <div className={tableClasses.metricsContainer}>
                    <div className={tableClasses.meritMetrics}>
                      <span>Cost of Merit Guidance</span>
                      <span className={classes.rightAlign}>
                        {getSimpleCashLabel(
                          estimatedCost ?? zero(CurrencyCode.USD)
                        )}
                      </span>
                    </div>
                  </div>
                </Tooltip>
              )}
            </TableCell>
          </TableRow>
          {matrixType === MatrixTypeEnum.MERIT && (
            <TableRow>
              <TableCell className={tableClasses.muiCellOverride} />
              <TableCell
                onClick={() =>
                  onToggleShouldPrefillWithGuidance(!shouldPrefillWithGuidance)
                }
                className={clsx(
                  tableClasses.muiCellOverride,
                  classes.prefillCell
                )}
                colSpan={orderedRanges.length}
              >
                <div className={classes.prefillContainer}>
                  Pre-fill merit adjustments with provided guidance
                  <Switch checked={shouldPrefillWithGuidance} />
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      ) : (
        "No data to display"
      )}
    </table>
  );
}

Matrix.fragments = {
  guide: gql`
    ${MatrixRangeCell.fragments.range}
    ${MatrixCell.fragments.perfRatingOption}
    fragment MatrixCard_matrixGuide on MatrixGuide {
      id
      percent
      matrixRange {
        id
        ...MatrixCard_matrixRange
      }
      matrixPerfRatingOption {
        id
        ...MatrixCard_matrixPerfRatingOption
      }
      matrixId
      matrixPerfRatingOptionId
      matrixRangeId
      organizationId
    }
  `,
};
